import {apiRequest} from '@/lib/api';
import { getDataClient } from "@/lib/dataClient";
import { useDataQuery, getCurrentUserId } from "@/hooks/useDataQuery";

// Öğretmen içerik yönetimi — dersler, ders programı ve video dersler.
// Tüm yazma işlemleri güvenli RPC'ler (SECURITY DEFINER) üzerinden yapılır.

export interface TeacherCourse {
  id: string;
  name: string;
  icon: string;
  color: string;
  totalTopics: number;
  description: string;
}

export interface TeacherVideoLesson {
  id: string;
  title: string;
  subject: string;
  duration: number;
  thumbnail: string;
  videoUrl: string;
}

export interface TeacherScheduleSlot {
  id: string;
  day: string;
  time: string;
  subject: string;
  type: string;
}

export const WEEK_DAYS = [
  "Pazartesi",
  "Salı",
  "Çarşamba",
  "Perşembe",
  "Cuma",
  "Cumartesi",
  "Pazar",
];

export const SCHEDULE_TYPES: { value: string; label: string }[] = [
  { value: "ders", label: "Ders" },
  { value: "canli", label: "Canlı Ders" },
  { value: "test", label: "Test / Deneme" },
];

export const SUBJECTS = [
  "Matematik",
  "Geometri",
  "Türkçe",
  "Fizik",
  "Kimya",
  "Biyoloji",
  "Tarih",
  "Coğrafya",
  "Felsefe",
  "İngilizce",
];

export const COURSE_ICONS = [
  "ri-calculator-line",
  "ri-shapes-line",
  "ri-book-open-line",
  "ri-flask-line",
  "ri-leaf-line",
  "ri-global-line",
  "ri-earth-line",
  "ri-lightbulb-line",
  "ri-quill-pen-line",
  "ri-translate-2",
];

export const COURSE_COLORS = [
  "bg-accent-100 text-accent-800",
  "bg-primary-100 text-primary-800",
  "bg-secondary-100 text-secondary-800",
  "bg-red-50 text-red-700",
  "bg-green-50 text-green-700",
  "bg-amber-50 text-amber-700",
];

async function resolveTeacherName(): Promise<string> {
  const dataClient = getDataClient();
  const { data } = await dataClient.auth.getUser();
  const metaName = data?.user?.user_metadata?.ad_soyad;
  return typeof metaName === "string" && metaName ? metaName : "Öğretmen";
}

export function useTeacherCourses() {
  const query = useDataQuery<TeacherCourse[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return [];
    const { data, error } = await dataClient
      .from("courses")
      .select("id, name, icon, color, total_topics, description")
      .eq("teacher_id", userId)
      .order("sort_order", { ascending: true });
    if (error) throw error;
    return (data ?? []).map(
      (c: { id: string; name: string; icon: string; color: string; total_topics: number; description: string | null }) => ({
        id: c.id,
        name: c.name,
        icon: c.icon,
        color: c.color,
        totalTopics: c.total_topics ?? 0,
        description: c.description ?? "",
      }),
    );
  });

  const saveCourse = async (input: {
    id?: string;
    name: string;
    icon: string;
    color: string;
    totalTopics: number;
    description: string;
  }) => {
    const dataClient = getDataClient();
    const teacherName = await resolveTeacherName();
    const { error } = await dataClient.rpc("teacher_upsert_course", {
      p_id: input.id ?? "",
      p_name: input.name,
      p_teacher_name: teacherName,
      p_icon: input.icon,
      p_color: input.color,
      p_total_topics: input.totalTopics,
      p_description: input.description,
    });
    if (error) throw error;
    query.reload();
  };

  const deleteCourse = async (id: string) => {
    const dataClient = getDataClient();
    const { error } = await dataClient.rpc("teacher_delete_course", { p_id: id });
    if (error) throw error;
    query.reload();
  };

  return { ...query, saveCourse, deleteCourse };
}

export function useTeacherVideoLessons() {
  const query = useDataQuery<TeacherVideoLesson[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return [];
    const { data, error } = await dataClient
      .from("video_lessons")
      .select("id, title, subject, duration_minutes, thumbnail, video_url")
      .eq("teacher_id", userId)
      .order("sort_order", { ascending: true });
    if (error) throw error;
    return (data ?? []).map(
      (v: { id: string; title: string; subject: string; duration_minutes: number | null; thumbnail: string | null; video_url: string | null }) => ({
        id: v.id,
        title: v.title,
        subject: v.subject,
        duration: v.duration_minutes ?? 0,
        thumbnail: v.thumbnail ?? "",
        videoUrl: v.video_url ?? "",
      }),
    );
  });

  const saveVideoLesson = async (input: {
    id?: string;
    title: string;
    subject: string;
    duration: number;
    thumbnail: string;
    videoUrl: string;
  }) => {
    await apiRequest('/mobile/save',{method:'POST',body:{table:'video_lessons',id:input.id??'',values:{title:input.title,subject:input.subject,duration_minutes:input.duration,thumbnail:input.thumbnail,video_url:input.videoUrl}}});
    query.reload();
  };

  const deleteVideoLesson = async (id: string) => {
    await apiRequest('/mobile/action',{method:'POST',body:{table:'video_lessons',id,action:'delete'}});
    query.reload();
  };

  return { ...query, saveVideoLesson, deleteVideoLesson };
}

export function useTeacherSchedule() {
  const query = useDataQuery<TeacherScheduleSlot[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return [];
    const { data, error } = await dataClient
      .from("schedule_slots")
      .select("id, day, time, subject, type")
      .eq("user_id", userId)
      .order("day", { ascending: true });
    if (error) throw error;
    return (data ?? []).map(
      (s: { id: string; day: string; time: string; subject: string; type: string }) => ({
        id: s.id,
        day: s.day,
        time: s.time,
        subject: s.subject,
        type: s.type,
      }),
    );
  });

  const addSlot = async (input: { day: string; time: string; subject: string; type: string }) => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return;
    const { error } = await dataClient.from("schedule_slots").insert({
      user_id: userId,
      day: input.day,
      time: input.time,
      subject: input.subject,
      type: input.type,
    });
    if (error) throw error;
    query.reload();
  };

  const deleteSlot = async (id: string) => {
    const dataClient = getDataClient();
    const { error } = await dataClient.from("schedule_slots").delete().eq("id", id);
    if (error) throw error;
    query.reload();
  };

  return { ...query, addSlot, deleteSlot };
}